/**
 * How fast the arm travels on the way up against the way down.
 *
 * Both halves cover the same 360 degrees of travel, so the only thing that
 * separates them is the time each takes, and the ratio of the two speeds is the
 * whole measurement.
 *
 * It reports and never judges. Driving the arm up faster than it comes down was
 * the original ask, and it was dropped as a criterion on 2026-10-03 because the
 * athlete could not reach any ratio the measurement can resolve: the spread
 * between clean reps is about 0.09, so a target set anywhere near 1.0 passes and
 * fails the same rep on different days. What survives is the figure itself, said
 * as "Up faster", "Down faster" or "Even", which is useful to watch across a set
 * and dishonest to fail a rep on.
 *
 * **Never per frame.** This is the one thing that will bite anyone changing this
 * file. The signed arm angle does not advance evenly between frames even when the
 * arm is moving smoothly, so a per-frame speed is mostly noise: on the reference
 * ten-rep take the median per-frame speed came out at 63 degrees/second against a
 * true average of 225. Speed here is therefore degrees travelled over a whole
 * phase divided by the time that phase took, which is also the quantity the
 * athlete can actually control.
 *
 * The up and down halves are split on `elevation`, which is bounded 0-180 by
 * construction, so the direction it moves cuts each revolution into exactly two
 * halves. This is the same split the amplitude metrics rely on, and it leaves the
 * rep counter's accumulated signed angle alone — see `revolutions.js` for why
 * those two signals must not borrow from each other.
 *
 * One idea that sounds right and is not: excluding a dead band around the top and
 * bottom of the circle, where elevation barely changes and the phase a frame
 * belongs to looks like it should be decided by noise. Tried on the reference
 * take across bands from 5 to 40 degrees. The spread between reps does not
 * improve — a standard deviation of 0.09 at no dead band against 0.09 at 10
 * degrees and 0.11 at 20 — and past 25 degrees the measurement falls apart
 * entirely, because what is left is the middle of each phase rather than the
 * phase. The spread is the movement and the landmarks, not the turnarounds.
 */

/** Smallest signed step between two angles, resolving the wrap at +/-180. */
function step(from, to) {
  let delta = to - from;
  while (delta > 180) {
    delta -= 360;
  }
  while (delta < -180) {
    delta += 360;
  }
  return delta;
}

/**
 * Degrees per second on the way up and on the way down, over one rep.
 *
 * @param samples One rep's samples, from `RepCounter`.
 * @param side Which arm, from `nearSide()`.
 * @param maxGapMs Frame gaps longer than this are dropped rather than bridged,
 *   for the same reason the counter drops them: a 490ms gap spans a quarter of a
 *   rep, and counting its degrees against its duration invents a speed.
 * @return `{ up, down, ratio }` in degrees/second, with `ratio` the up speed over
 *   the down speed — above 1 means the arm went up faster. Any of them is null
 *   when a phase has no usable frames.
 */
export function repTempo(samples, side, maxGapMs = 250) {
  let upDegrees = 0;
  let upMs = 0;
  let downDegrees = 0;
  let downMs = 0;

  for (let i = 1; i < samples.length; i += 1) {
    const elapsed = samples[i].t - samples[i - 1].t;
    if (elapsed <= 0 || elapsed > maxGapMs) {
      continue;
    }
    const degrees = Math.abs(
      step(samples[i - 1].armAngle[side], samples[i].armAngle[side])
    );
    // Equal elevation counts as rising, so a frame is never discarded for being
    // exactly level; at this sample rate that is a handful of frames per rep.
    if (samples[i].elevation[side] >= samples[i - 1].elevation[side]) {
      upDegrees += degrees;
      upMs += elapsed;
    } else {
      downDegrees += degrees;
      downMs += elapsed;
    }
  }

  const up = upMs > 0 ? (1000 * upDegrees) / upMs : null;
  const down = downMs > 0 ? (1000 * downDegrees) / downMs : null;
  return {
    up,
    down,
    ratio: up != null && down != null && down > 0 ? up / down : null,
  };
}

/**
 * How fast the arm travels over the whole rep, in degrees per second.
 *
 * **Net rotation, not path length.** The steps are summed signed and the total's
 * magnitude taken at the end, so that jitter which goes one way and comes back
 * cancels instead of counting twice. Summing each step's magnitude instead — the
 * obvious way to write this, and the way it was written first — makes a rep where
 * the athlete stood almost still read as fast: on the reference take the paused
 * rep came out at 217 degrees/second against the 95 it actually turned, because
 * nearly four seconds of landmark jitter accumulated as travel. That destroys the
 * very separation this criterion exists to make.
 *
 * The up-and-down split in `repTempo` is different and does want magnitudes:
 * there the frames of a phase all turn the same way, so there is no cancelling to
 * do, and the two halves are compared against each other rather than against an
 * absolute floor.
 *
 * Elapsed time is measured only across the frames that supplied the degrees, so a
 * dropped frame costs both its travel and its milliseconds rather than inventing
 * a slow rep.
 */
export function repSpeed(samples, side, maxGapMs = 250) {
  let net = 0;
  let elapsed = 0;
  for (let i = 1; i < samples.length; i += 1) {
    const dt = samples[i].t - samples[i - 1].t;
    if (dt <= 0 || dt > maxGapMs) {
      continue;
    }
    net += step(samples[i - 1].armAngle[side], samples[i].armAngle[side]);
    elapsed += dt;
  }
  return elapsed > 0 ? (1000 * Math.abs(net)) / elapsed : null;
}

/**
 * The speed criterion: a rep that is too slow fails.
 *
 * Unlike the ratio, this one separates cleanly. On the reference take the eight
 * reps performed at pace run 186 to 225 degrees/second, while the two that are
 * only a rep because the athlete was walking into frame or had paused mid-set
 * come out at 74 and 95. Nothing lands in between, so any threshold from 120 to
 * 180 splits them identically and the choice of 150 is the middle of that gap
 * rather than a measurement sitting on a boundary.
 *
 * @param thresholds `STATE.posture`; `swingSpeedMin` is the floor in degrees per
 *   second.
 */
export function scoreSpeed(samples, side, thresholds) {
  const speed = repSpeed(samples, side, thresholds.maxGapMs);
  return {
    id: "swing_speed",
    label: "Swing speed",
    value: speed,
    unit: "°/s",
    precision: 0,
    pass: speed == null ? null : speed >= thresholds.swingSpeedMin,
  };
}

/**
 * The up/down tempo row: informational, never a verdict.
 *
 * The same row shape as the criteria so it can be drawn by the same code, but
 * marked `informational` and with `pass` permanently null, which is what keeps it
 * out of the cues and out of the pass tally. A row cannot carry that by `pass`
 * alone: null already means "the landmarks were guesses, so this rep was not
 * judged", and reusing it here would make every clean rep report itself as only
 * partly seen.
 *
 * `note` is the whole point of the row. Which way the figure leans is only said
 * when it leans further than the measurement can resolve — `tempoEvenBand`, 0.10,
 * the measured spread between clean reps — so inside that band it says "Even"
 * rather than inventing a difference out of noise.
 *
 * Kept out of `posture.js` because it is not posture: posture is what the body
 * holds, this is how the movement is paced.
 *
 * @param thresholds `STATE.posture`; `tempoEvenBand` is how far from 1.0 the
 *   ratio has to sit before either side is called faster.
 */
export function scoreTempo(samples, side, thresholds) {
  const { ratio } = repTempo(samples, side, thresholds.maxGapMs);
  return {
    id: "tempo",
    label: "Up vs down",
    value: ratio,
    unit: "×",
    precision: 2,
    informational: true,
    note: tempoNote(ratio, thresholds.tempoEvenBand),
    pass: null,
  };
}

/** Which way the ratio leans, or "Even" when it is inside the noise. */
export function tempoNote(ratio, evenBand = 0.1) {
  if (ratio == null) {
    return null;
  }
  if (ratio >= 1 + evenBand) {
    return "Up faster";
  }
  if (ratio <= 1 - evenBand) {
    return "Down faster";
  }
  return "Even";
}
